import { useState } from 'react';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { DataField, ExtractionFieldType } from '@/types/automationFields.ts';

const extractionTypeOptions = Object.values(ExtractionFieldType).map((type) => ({
  label: type,
  value: type,
}));

type DataFieldTypeCellProps = {
  field: DataField;
  update: (field: Partial<DataField>) => void;
};

export default function DataFieldTypeCell({ field, update }: DataFieldTypeCellProps) {
  const [pendingType, setPendingType] = useState<ExtractionFieldType | null>(null);

  const applyType = (field_type: ExtractionFieldType, resetCustomMissingValue: boolean) => {
    update({
      field_type,
      // A custom value may not be valid for the new type. Reset only the new
      // contract; untouched legacy defaults keep round-tripping.
      ...(resetCustomMissingValue ? { default_value: null, missing_value: { kind: 'type_empty' } } : {}),
      // The backend rejects this policy on non-numeric fields. Clear it as
      // part of a type change so a previously valid Number field continues to
      // round-trip as a valid contract.
      ...(field_type !== ExtractionFieldType.Number ? { blank_as_zero_allowed: false } : {}),
    });
  };

  const handleChange = (value: string | null) => {
    if (!value) return;
    const field_type = value as ExtractionFieldType;
    const resetsCustomMissingValue =
      field_type !== field.field_type &&
      (field.missing_value?.kind === 'value' || (field.missing_value == null && field.default_value !== null && field.default_value !== ''));
    if (resetsCustomMissingValue) {
      setPendingType(field_type);
      return;
    }
    applyType(field_type, false);
  };

  return (
    <>
      <StyledSelect
        aria-label="Field type"
        data={extractionTypeOptions}
        value={field.field_type}
        onChange={handleChange}
        required={true}
        clearable={false}
        allowDeselect={false}
        styles={{ dropdown: { minWidth: '120px' } }}
      />
      <ConfirmationModal
        opened={pendingType !== null}
        onClose={() => setPendingType(null)}
        title="Replace custom missing value?"
        message="Changing the field type will replace its custom missing value with the platform default."
        confirmText="Change type"
        onConfirm={() => {
          if (pendingType) applyType(pendingType, true);
        }}
      />
    </>
  );
}
